import axios from 'axios'

import store from '../redux/store/store'
import { hide, show } from '../redux/slice/SettingSlice'
import { BaseURL } from '../admin/helper/Config'
import { getToken, removeSessions } from '../admin/helper/SessionHelper'
import { getActiveShopId, setActiveShopId } from '../shop/activeShop'
import { isAuthFailure } from './errors'

/**
 * The one axios instance every service module calls through (T-016).
 *
 * WHAT WAS WRONG
 *
 * All seventeen service modules built their auth header once, at module load:
 *
 *     const AxiosHeader = { headers: { Authorization: `Bearer ${getToken()}` } }
 *
 * A module body runs once, the first time something imports it. So the token
 * baked into that header is whatever was in localStorage at that moment, and it
 * never changes again for the life of the page.
 *
 * Today that happens to work, and only because logging in does
 * `window.location.href = "/dashboard"` — a full page load, which throws away
 * every module and re-evaluates them all against the fresh token. The moment
 * anyone replaces that with a router navigation, every service module in the
 * app starts sending `Bearer null` and nothing works. It is a trap laid for
 * whoever does the obvious cleanup.
 *
 * Reading the token inside a request interceptor means it is read per request,
 * from wherever it currently is, and the trap is gone.
 *
 * AND THE THING NOBODY HANDLED AT ALL
 *
 * There was no 401 handling anywhere in this codebase. When a token expired —
 * which it does, on its own schedule, usually mid-shift — every screen fell
 * into its catch block and showed "Something Went Wrong". Staff were left on a
 * page that looked fine, with a Save button that failed every time, and no
 * indication that the fix was to log in again. A response interceptor turns
 * that into what it actually is: the session ended, here is the login screen.
 */

const api = axios.create({
    baseURL: BaseURL,
})

/**
 * Deliberately no default Content-Type.
 *
 * Four of the services set one explicitly and the rest send none, letting axios
 * choose — which it does correctly: JSON for plain objects, and for FormData the
 * multipart type WITH the boundary parameter that a hand-written
 * "multipart/form-data" leaves out. Forcing a default here would override those
 * call sites and break the product image upload. Call sites keep whatever they
 * were passing.
 */

/**
 * The global loading bar, and which requests are allowed to raise it (T-025).
 *
 * It used to go up for EVERY request, dispatched by hand in 87 places across
 * the service modules. That is wrong in both directions.
 *
 * A read should not raise it. Opening the product list put a bar across the top
 * of the whole application to say that the product list was loading — which the
 * product list can say for itself, in the space where the rows will appear, so
 * the layout does not jump when they arrive. A global bar for a local wait also
 * makes the whole app feel blocked when only one region is.
 *
 * A WRITE should. Saving an order is blocking: the user must not start a second
 * one, and there is no region to put the message in because the region is what
 * is being changed.
 *
 * So the rule lives here rather than in 87 call sites, where it drifted.
 */
function isBlockingWrite(config) {
    const method = (config?.method || 'get').toLowerCase()
    return method !== 'get' && method !== 'head' && method !== 'options'
}

/**
 * Which shop this request is for (BE-02).
 *
 * Read here, per request, for exactly the reason the token is: the active shop
 * changes mid-session when someone uses the picker, and a value captured at
 * module load would keep filing orders against the shop they started the day
 * in.
 *
 * Sending nothing is a legitimate state, not a failure — see activeShop.js. A
 * user entitled to one shop needs no header, and before GET /shops has answered
 * we do not know enough to send one. What we must never do is guess: the
 * backend trusts this header to decide which shop's books a sale lands in, and
 * an entitled-but-wrong id is accepted silently.
 *
 * Call sites can still override it. Nothing does today, but a cross-shop report
 * that deliberately spans everything the user can see would want the header
 * absent even when a shop is selected, and setting it here unconditionally
 * would take that away.
 */
function applyShopHeader(config) {
    if (config.headers?.['X-Shop-Id'] !== undefined) return

    const shopId = getActiveShopId()
    if (shopId) config.headers['X-Shop-Id'] = shopId
}

api.interceptors.request.use((config) => {
    // Per request, not per module load. That is the whole point.
    const token = getToken()

    if (token) {
        config.headers.Authorization = `Bearer ${token}`
    }

    applyShopHeader(config)

    if (isBlockingWrite(config)) {
        store.dispatch(show())
        // Marked on the config so the response side hides exactly what it
        // showed. Deciding again from the method would work today and break the
        // first time a redirect or a retry changes it.
        config.__showedLoader = true
    }

    return config
})

/**
 * Only one redirect, however many requests fail at once.
 *
 * A screen mount fires up to six requests concurrently (T-014). An expired token
 * fails all six, and without this every one of them would clear the session and
 * assign window.location — six navigations racing each other, and five of them
 * clearing storage that the first redirect already depends on.
 */
let sessionEndedAlready = false

export function resetSessionEndGuard() {
    // Test seam. The flag is module state by design — it must persist across
    // requests within a page — so tests need a way back to a known start.
    sessionEndedAlready = false
}

/**
 * Is this 403 "you were removed from that shop", rather than an ordinary
 * permission refusal?
 *
 * Matched on the message because the API's error envelope reports both as
 * `error: "Permission"` and there is no narrower code to key on. Keeping the
 * match here, in one place, means the day the backend does add a distinct code
 * there is a single line to change — and matching too loosely only costs a
 * redundant re-fetch of the entitlement list, never a lost session.
 */
function isRevokedShop(body) {
    const message = String(body?.message || '')
    return message.includes('do not have access to that shop')
        || message.includes('not assigned to any shop')
}

let shopRevocationHandled = false

/**
 * Drop the revoked shop and send the user somewhere that works.
 *
 * Guarded, because a page that fired six requests on mount would otherwise
 * reload six times. Cleared by the reload itself.
 */
function handleShopRevoked() {
    if (shopRevocationHandled) return
    shopRevocationHandled = true

    // Dropping the selection is not enough on its own: reconcileShops also
    // clears the permissions cached for that shop and purges its drafts, so a
    // half-finished invoice for a branch the user no longer works in cannot
    // resurface under another shop's name.
    setActiveShopId(null)

    // Re-ask the server which shops they may still work in. The reload then
    // puts them in front of the chooser — or the no-shop banner, if that was
    // their last shop — instead of a screen quietly 403-ing in the background.
    //
    // Imported lazily: ShopApiService imports THIS module for its axios
    // instance, so a top-level import would be a cycle, and under Jest a cycle
    // resolves to undefined rather than throwing — the reload would happen but
    // the entitlement list would never be refreshed, and the chooser would
    // offer the shop the user was just removed from.
    import('../admin/apiServices/ShopApiService')
        .then(({ GetShopList }) => GetShopList())
        .catch(() => {})
        .finally(() => { window.location.reload() })
}

export function __resetShopRevocationForTests() {
    shopRevocationHandled = false
}

api.interceptors.response.use(
    (response) => {
        if (response?.config?.__showedLoader) store.dispatch(hide())
        return response
    },
    (error) => {
        // Before anything else. An error path that forgets to hide leaves the
        // bar up for the rest of the session — and since T-014 made it a
        // counter, one missed hide means it never comes down again.
        if (error?.config?.__showedLoader) store.dispatch(hide())

        const status = error?.response?.status
        // Was this a request we made ON BEHALF of a logged-in user? Login and
        // the password-recovery calls carry no token, and a rejection from
        // those means "wrong password" or "bad code" — a message for the user
        // to read, not a session to end. Redirecting there would bounce someone
        // off the login page they are already standing on, discarding what they
        // typed.
        //
        // (On this backend a bad login is a 404 — API_REFERENCE §2 keeps it
        // that way because the SPA depends on it. So this guard is defensive
        // rather than load-bearing for login. It still matters for password
        // recovery.)
        const wasAuthenticated = Boolean(error?.config?.headers?.Authorization)

        // ⚠️ AN EXPIRED TOKEN HERE IS A 400, NOT A 401.
        //
        // API_REFERENCE §1 and §18.8: the auth middleware writes its own
        // response and never reaches the handler that would have made it 401,
        // so a missing or expired token arrives as HTTP 400 with
        // `error: "error"`. The backend is keeping it that way rather than
        // break clients already keying on 400 — this is a permanent shape, not
        // something to code around until it is fixed.
        //
        // Keying on the status alone would be wrong in both directions: 401 is
        // never sent, and a bare 400 also means a malformed request. So the
        // decision lives in isAuthFailure, which reads the body — and it is
        // SHARED with classifyApiError, so the toast the user reads and the
        // redirect that fires can never disagree about whether the session has
        // ended.
        //
        // Before BE-04 this branch was unreachable for a different reason:
        // every failure was a 500, so a session expiring mid-shift showed
        // "Something Went Wrong" on every action, on a page that otherwise
        // looked fine, with nothing to suggest the fix was to log in again.
        if (isAuthFailure(status, error?.response?.data) && wasAuthenticated && !sessionEndedAlready) {
            sessionEndedAlready = true
            // Clears localStorage and sends the browser to /login.
            removeSessions()
        }

        // A shop the user is no longer entitled to.
        //
        // ResolveShop answers "You do not have access to that shop." when
        // X-Shop-Id names a shop they have been removed from — which happens
        // MID-SHIFT, with the id sitting in localStorage and going out on every
        // request. Without this, the user gets that toast on every single call
        // and has no way out but a manual refresh, because nothing on the page
        // ever re-reads the entitlement list.
        //
        // NOT a logout. Being removed from one branch is not being removed from
        // the system, and they may well still work in another.
        if (status === 403 && wasAuthenticated && isRevokedShop(error?.response?.data)) {
            handleShopRevoked()
        }

        return Promise.reject(error)
    }
)

/**
 * For requests made by someone who is NOT logged in: sign-in, and the three
 * password-recovery steps.
 *
 * It gets the loading bar — logging in is as blocking as any write — but
 * neither the auth header nor the 401 handling. That separation is the point:
 * if these went through the authenticated client and a stale token happened to
 * be sitting in localStorage, a wrong-password 401 would clear the session and
 * redirect to the login page the user is already standing on, throwing away
 * what they typed. A redirect loop dressed as a login form.
 */
export const publicApi = axios.create({ baseURL: BaseURL })

publicApi.interceptors.request.use((config) => {
    if (isBlockingWrite(config)) {
        store.dispatch(show())
        config.__showedLoader = true
    }
    return config
})

publicApi.interceptors.response.use(
    (response) => {
        if (response?.config?.__showedLoader) store.dispatch(hide())
        return response
    },
    (error) => {
        if (error?.config?.__showedLoader) store.dispatch(hide())
        return Promise.reject(error)
    }
)

export default api
